import { components } from "@/api-schema";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ButtonColorVariant } from "@/components/ui/button";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { MultiSelect } from "@/components/ui/multi-select";
import { TriangleAlert } from "lucide-react";
import { useMemo } from "react";
import { Control, FieldError, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export const formSchema = z
  .object({
    companies: z.array(z.string()).optional(),
    companyGroups: z.array(z.string()).optional(),
    branches: z.array(z.string()).optional(),
    occupations: z.array(z.string()).optional(),
    departments: z.array(z.string()).optional(),
    brands: z.array(z.string()).optional(),
    jobFunctions: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      return (
        (data.companies?.length ?? 0) > 0 ||
        (data.companyGroups?.length ?? 0) > 0 ||
        (data.branches?.length ?? 0) > 0 ||
        (data.occupations?.length ?? 0) > 0 ||
        (data.departments?.length ?? 0) > 0 ||
        (data.brands?.length ?? 0) > 0 ||
        (data.jobFunctions?.length ?? 0) > 0
      );
    },
    {
      message: "ram:AtLeastOneRuleMustBeSet",
    },
  );

export type formSchemaType = z.infer<typeof formSchema>;

type RuleFormFieldsType = {
  isLoading: boolean;
  formControl: Control<formSchemaType>;
  companies: components["schemas"]["Company"][] | undefined;
  companyGroups: components["schemas"]["CompanyGroup"][] | undefined;
  branches: components["schemas"]["Branch"][] | undefined;
  departments: components["schemas"]["Department"][] | undefined;
  occupations: components["schemas"]["Occupation"][] | undefined;
  brands: components["schemas"]["Brand"][] | undefined;
  jobFunctions: components["schemas"]["JobFunction"][] | undefined;
};

export function RuleFormFields({
  formControl,
  companies,
  companyGroups,
  branches,
  departments,
  occupations,
  brands,
  jobFunctions,
}: RuleFormFieldsType) {
  const { t } = useTranslation(["common", "ram"]);
  const {
    formState: { errors },
  } = useFormContext<formSchemaType>();

  const mappedCompanies = useMemo(
    () => companies?.map((company) => ({ value: company.uuid, label: company.name })),
    [companies],
  );
  const mappedCompanyGroups = useMemo(
    () => companyGroups?.map((companyGroup) => ({ value: companyGroup.uuid, label: companyGroup.name })),
    [companyGroups],
  );
  const mappedBranches = useMemo(
    () =>
      branches?.map((branch) => ({
        value: branch.uuid,
        label: branch.name,
      })),
    [branches],
  );
  const mappedDepartments = useMemo(
    () => departments?.map((department) => ({ value: department.uuid, label: department.name })),
    [departments],
  );
  const mappedOccupations = useMemo(
    () => occupations?.map((occupation) => ({ value: occupation.uuid, label: occupation.name })),
    [occupations],
  );
  const mappedBrands = useMemo(() => brands?.map((brand) => ({ value: brand.uuid, label: brand.name })), [brands]);
  const mappedJobFunctions = useMemo(
    () => jobFunctions?.map((jobFunction) => ({ value: jobFunction.uuid, label: jobFunction.name })),
    [jobFunctions],
  );

  return (
    <>
      <FormField
        control={formControl}
        name="companyGroups"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("common:CompanyGroups")}</FormLabel>
            <MultiSelect
              options={mappedCompanyGroups ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("ram:SelectCompanyGroups")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={formControl}
        name="companies"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("common:Companies")}</FormLabel>
            <MultiSelect
              options={mappedCompanies ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("common:SelectCompanies")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={formControl}
        name="branches"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("common:Branches")}</FormLabel>
            <MultiSelect
              options={mappedBranches ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("ram:SelectBranches")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={formControl}
        name="departments"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("ram:Departments")}</FormLabel>
            <MultiSelect
              options={mappedDepartments ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("ram:SelectDepartments")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={formControl}
        name="occupations"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("ram:Occupations")}</FormLabel>
            <MultiSelect
              options={mappedOccupations ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("ram:SelectOccupations")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={formControl}
        name="brands"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("ram:Brands")}</FormLabel>
            <MultiSelect
              options={mappedBrands ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("ram:SelectBrands")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={formControl}
        name="jobFunctions"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel>{t("ram:JobFunctions")}</FormLabel>
            <MultiSelect
              options={mappedJobFunctions ?? []}
              onValueChange={field.onChange}
              value={field.value}
              defaultValue={field.value}
              placeholder={t("ram:SelectJobFunctions")}
              variant={ButtonColorVariant.Secondary}
              maxCount="auto"
            />
            <FormMessage />
          </FormItem>
        )}
      />

      {(errors.root || (errors as Record<string, FieldError | undefined>)[""]) && (
        <Alert variant={ButtonColorVariant.Error} className="mb-6">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>{t("ram:Attention")}</AlertTitle>
          <AlertDescription>
            {t((errors.root?.message || (errors as Record<string, FieldError | undefined>)[""]?.message)!)}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
}
